<script setup lang="ts">
    import EventIcon from './EventIcon.vue';
    import { computed, inject, reactive, ref, watch } from 'vue';
    import EmptyState from '@shared/components/EmptyState.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import Loading from '@shared/components/Loading.vue';
    import Preview from '@shared/components/Preview.vue';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import TestSendModal from '@views/Event/TestSendModal.vue';
    import EventConfirmationContent, { TestSendStatus } from '@views/Event/EventConfirmationContent.vue';
    import { setupEventValidation } from '@views/Event/Event';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { type Issue } from '@views/Issues/interfaces';
    import { loadIssueSummary } from '@views/Event/Event.access';
    import { type Event, type EventCommunicationKey, EventStatusNames, eventCommunicationKeys } from '@views/Event/EventTypes';
    import { eventKey, queueEventPropertyKey, canEditKey, canWriteKey } from '@views/Event/EventEditor/event-editor-keys';

    export interface IssueConfirmation {
        title: string;
        description: string;
        contentTitle: string;
        subjectKey: EventCommunicationKey;
        textKey: EventCommunicationKey;
        status: TestSendStatus;
    }

    /** Shared by the confirmations and attestation pages: one issue on the event, plus the message texts sent with it. */
    const props = defineProps<{
        eventId: string;
        issueIdKey: 'ParticipantStatusChangeIssueId' | 'AttestationIssueId';
        confirmations: IssueConfirmation[];
        /** Query flag telling the issue editor which event issue it creates. */
        newIssueQueryKey: string;
        addDefaultIssue: (eventId: string) => Promise<{ IssueId: string; Event: Event }>;
        deleteIssue: (eventId: string) => Promise<void>;
        heading: string;
        description: string;
        missingHeading: string;
        missingText: string;
        missingStandardBtnText: string;
        missingCustomBtnText: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const canWrite = inject(canWriteKey)!;
    const queueEventProperty = inject(queueEventPropertyKey)!;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive<{
        issue: Issue | null;
        isLoading: boolean;
        isAdding: boolean;
        isDeleting: boolean;
    }>({
        issue: null,
        isLoading: false,
        isAdding: false,
        isDeleting: false,
    });

    const isActive = computed(() => event.value?.Status === EventStatusNames.Active);
    const issueId = computed(() => event.value?.[props.issueIdKey] ?? null);

    let loadingIssueId: string | null = null;
    async function loadIssue(id: string | null) {
        loadingIssueId = id;
        state.issue = null;
        if (!id) return;
        state.isLoading = true;
        try {
            const issue = await loadIssueSummary(id);
            if (loadingIssueId === id) state.issue = issue;
        } catch {
            if (loadingIssueId === id) bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorTechnicalIssuesBody });
        } finally {
            if (loadingIssueId === id) state.isLoading = false;
        }
    }

    watch(issueId, loadIssue, { immediate: true });

    // Local copies of the texts shown in the editors.
    const texts = reactive({} as Record<EventCommunicationKey, string>);
    watch(
        () => eventCommunicationKeys.map((key) => event.value?.[key]),
        (values, oldValues) =>
            eventCommunicationKeys.forEach((key, i) => {
                if (values[i] !== oldValues?.[i]) texts[key] = values[i] ?? '';
            }),
        { immediate: true }
    );

    function onTextChange(key: EventCommunicationKey, newValue: string, label: string) {
        texts[key] = newValue;
        queueEventProperty(key, newValue, label);
    }

    // Parity with legacy: adding is allowed on Active events (only deleting is blocked), but requires Write access.
    async function addDefaultIssue() {
        if (!canWrite.value) return;
        state.isAdding = true;
        try {
            const response = await props.addDefaultIssue(props.eventId);
            // Parity with EditEventViewModel.updateEventCommunicationDefaults: adopt the server's default texts without re-saving them.
            for (const key of eventCommunicationKeys) event.value![key] = response.Event[key];
            event.value![props.issueIdKey] = response.IssueId;
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isAdding = false;
        }
    }

    async function deleteIssue() {
        if (isActive.value) return;
        state.isDeleting = true;
        try {
            await props.deleteIssue(props.eventId);
            event.value![props.issueIdKey] = null;
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isDeleting = false;
        }
    }

    const stats = computed(() => {
        const issue = state.issue;
        if (!issue) return [];
        return [
            {
                label: $Resources.IssuesSentLabel,
                value: formatNumericText(issue.SentCount, { largeNumbers: true }),
                width: issue.SentCount > 0 ? 100 : 0,
                barClass: 'bg-sent',
            },
            {
                label: $Resources.IssuesReceivedPercentageLabel,
                value: formatNumericText(issue.ReceivedPercentage, { suffix: '%' }),
                width: issue.ReceivedPercentage,
                barClass: 'bg-received',
            },
            {
                label: $Resources.IssuesOpenedPercentageLabel,
                value: formatNumericText(issue.OpenPercentage, { suffix: '%' }),
                width: issue.OpenPercentage,
                barClass: 'bg-opened',
            },
            {
                label: $Resources.IssuesClickedPercentageLabel,
                value: formatNumericText(issue.ClickPercentage, { suffix: '%', precision: issue.ClickPercentage > 0 && issue.ClickPercentage < 1 ? 1 : 0 }),
                width: issue.ClickPercentage,
                barClass: 'bg-clicked',
            },
        ];
    });

    const eventIsPublishable = setupEventValidation({
        name: computed(() => event.value?.Name ?? ''),
        participantStatusChangeIssueId: computed(() => event.value?.ParticipantStatusChangeIssueId ?? ''),
        defaultSurveyId: computed(() => event.value?.DefaultSurveyId ?? ''),
        requireAttestation: computed(() => !!event.value?.RequireAttestation),
        attestationIssueId: computed(() => event.value?.AttestationIssueId ?? ''),
    });

    const testSendStatus = ref<string>('');
    const showTestSendModal = ref(false);
    function openTestSendModal(status: string) {
        testSendStatus.value = status;
        showTestSendModal.value = true;
    }

    const previewUrl = ref<string | null>(null);
    function openPreview(status: string) {
        if (!issueId.value) return;
        const publicHost = contextAccess.state.currentAccount!.PublicEventUrl.PublicHost;
        previewUrl.value = combineUrlSegments(publicHost, 'Issues', issueId.value, `?preview=true&eventId=${props.eventId}&participantStatus=${status}`);
    }

    const newIssueRoute = computed(() => ({ name: 'IssuesNew', query: { eventId: props.eventId, [props.newIssueQueryKey]: 'true' } }));
</script>

<template>
    <div v-if="event" class="max-900">
        <h2 class="h3 m-0-t">{{ heading }}</h2>
        <p class="m-lg-b">{{ description }}</p>

        <EmptyState v-if="!issueId" class="m-lg-b" :title="missingHeading" :body="missingText">
            <template #actions>
                <div class="form-group m-sm-b">
                    <button type="button" class="btn btn-info" :class="{ 'is-busy': state.isAdding }" :disabled="!canWrite || state.isAdding" @click="addDefaultIssue">
                        {{ missingStandardBtnText }}
                        <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </button>
                </div>
                <div class="form-group m-sm-b">
                    <RouterLink class="btn btn-subtle" :class="{ disabled: !canWrite }" :to="newIssueRoute">
                        {{ missingCustomBtnText }}
                        <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </RouterLink>
                </div>
            </template>
        </EmptyState>

        <div v-else>
            <Loading v-if="state.isLoading" loadingStyle="progress-bar" show fixed />

            <div v-if="state.issue" class="m-xl-b">
                <div class="d-flex flex-wrap align-items-end gap-xs m-lg-b" role="toolbar">
                    <a :href="'/Issues/' + state.issue.IssueId" class="btn btn-primary btn-flex" :class="{ disabled: !canEdit }">
                        <EventIcon icon="editSquare" :size="16" />
                        {{ $Resources.EventSelectedConfirmationEditBtnText }}
                    </a>

                    <RouterLink
                        v-if="state.issue.SentCount > 0"
                        class="btn btn-default btn-flex"
                        :to="{ name: 'IssueStatistics', params: { issueId: state.issue.IssueId } }">
                        <EventIcon icon="barChart" :size="16" />
                        {{ $Resources.IssuesViewStatistic }}
                    </RouterLink>

                    <div class="d-flex align-items-center gap-sm m-auto-l">
                        <p v-if="canEdit && isActive" class="m-0-b">{{ $Resources.EventMustBeUnpublishedInfo }}!</p>

                        <ConfirmButton
                            :button-text="$Resources.EventSelectedConfirmationRemoveBtnText"
                            :disabled="!canEdit || isActive || state.isDeleting"
                            undo-button
                            @confirm="deleteIssue" />
                    </div>
                </div>

                <div v-if="state.issue.SentCount > 0" class="stats-cards ug-bars d-flex flex-wrap m-lg-b">
                    <div v-for="stat in stats" :key="stat.label" class="col-md-3 col-sm-6 col-xs-12">
                        <p>{{ stat.label }}</p>
                        <p class="stat">{{ stat.value }}</p>
                        <div class="bar">
                            <span class="bar-value" :class="stat.barClass" :style="{ width: stat.width + '%' }"></span>
                        </div>
                    </div>
                </div>

                <p v-if="state.issue.IssueName">{{ state.issue.IssueName }}</p>

                <TagsList v-if="state.issue.Tags?.length" :tags="state.issue.Tags" readonly />

                <p class="item-meta d-flex flex-wrap col-gap-lg row-gap-sm m-lg-b">
                    <span class="d-flex gap-xs">
                        <EventIcon icon="editSquare" :size="16" />
                        <span>
                            {{ $Resources.LastModifiedLabel }}
                            <time :datetime="state.issue.LastModified">{{ formatDateTime(state.issue.LastModified) }}</time>
                            {{ $Resources.GenericByLabel }}
                            {{ state.issue.LastModifiedBy?.FirstName }} {{ state.issue.LastModifiedBy?.LastName }}
                        </span>
                    </span>
                </p>
            </div>

            <EventConfirmationContent
                v-for="confirmation in confirmations"
                :key="confirmation.status"
                :updateEnabled="canEdit"
                :title="confirmation.title"
                :description="confirmation.description"
                :subject="texts[confirmation.subjectKey]"
                :contentTitle="confirmation.contentTitle"
                :content="texts[confirmation.textKey]"
                :testSendStatus="confirmation.status"
                @update:subject="(newValue: string) => onTextChange(confirmation.subjectKey, newValue, $Resources.SubjectLabel)"
                @update:content="(newValue: string) => onTextChange(confirmation.textKey, newValue, confirmation.contentTitle)"
                @openTestSendModal="openTestSendModal"
                @openPreview="openPreview" />
        </div>

        <TestSendModal
            :eventId="eventId"
            :status="testSendStatus"
            :show="showTestSendModal"
            :eventIsPublishable="eventIsPublishable"
            :name="event.Name"
            :defaultSurveyId="event.DefaultSurveyId ?? ''"
            :participantStatusChangeIssueId="event.ParticipantStatusChangeIssueId ?? ''"
            :requireAttestation="event.RequireAttestation"
            :attestationIssueId="event.AttestationIssueId ?? ''"
            @update:show="showTestSendModal = false" />

        <Preview :previewUrl="previewUrl" @close="previewUrl = null" />
    </div>
</template>
